/**
 * FlowNotes API Client - Voice Memos
 * Voice memo upload, processing, and management functions
 */

import { fetchWithAuth, API_BASE_URL } from './core';

export interface VoiceMemo {
  id: string;
  filename: string;
  title?: string;
  summary?: string;
  duration: string;
  date: string;
  status: 'unprocessed' | 'transcribing' | 'transcribed' | 'analyzed' | 'filed' | 'ignored';
  fileSize: string;
  source?: 'uploaded' | 'system' | 'system-copied';
  hasTranscript?: boolean;
  transcript?: {
    filename: string;
    path: string;
    wordCount: number;
    duration: string;
  };
  session?: {
    id: number;
    index: number;
    total: number;
    date: string;
  };
}

export interface VoiceMemoUploadResponse {
  success: boolean;
  uploaded: number;
  failed: number;
  files: Array<{
    filename: string;
    originalName?: string;
    size: number;
    path: string;
    status: string;
  }>;
  errors?: Array<{
    filename: string;
    error: string;
  }>;
}

export interface FiledVoiceMemoDocument {
  id: string;
  title?: string;
  filePath?: string;
  webappUrl?: string;
}

export interface VoiceMemoProcessResponse {
  success: boolean;
  filename: string;
  transcript: {
    text?: string;
    filename: string;
    path: string;
    wordCount: number;
    duration: string;
  };
  document?: FiledVoiceMemoDocument | null;
  message: string;
}

export interface VoiceMemoListResponse {
  success: boolean;
  total: number;
  memos: VoiceMemo[];
}

const VOICE_MEMO_API_BASE = import.meta.env.VITE_VOICE_MEMO_API_BASE_URL || API_BASE_URL;

/**
 * List voice memos with optional filtering
 */
export async function getVoiceMemos(params?: {
  status?: string;
  tab?: string;
  source?: string;
  limit?: number;
}): Promise<VoiceMemoListResponse & { counts?: { total: number; unprocessed: number; analyzed: number; filed: number; ignored: number } }> {
  const queryParams = new URLSearchParams();
  if (params?.tab) {
    queryParams.append('tab', params.tab);
  } else if (params?.status && params.status !== 'all') {
    queryParams.append('tab', params.status);
  }
  if (params?.source) queryParams.append('source', params.source);
  if (params?.limit) queryParams.append('limit', params.limit.toString());

  const url = `${VOICE_MEMO_API_BASE}/api/voice-memos/discover?${queryParams}`;
  console.log('[getVoiceMemos] Fetching from:', url);

  const response = await fetchWithAuth(url, {
    credentials: 'include'
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('[getVoiceMemos] Error response:', response.status, errorText);
    throw new Error(`Failed to fetch voice memos: ${response.statusText}`);
  }

  const data = await response.json();
  console.log('[getVoiceMemos] Raw API response:', {
    success: data.success,
    tab: data.tab,
    counts: data.counts,
    memosCount: data.memos?.length || 0,
    firstMemo: data.memos?.[0] ? {
      keys: Object.keys(data.memos[0]),
      filename: data.memos[0].filename,
      status: data.memos[0].status,
      hasStats: !!data.memos[0].stats,
      hasMetadata: !!data.memos[0].metadata,
      hasParsedDate: !!data.memos[0].parsedDate
    } : null
  });

  const transformedMemos = (data.memos || []).map((memo: any) => {
    const id = memo.id || memo.filename?.replace(/\.m4a$/, '') || memo.filename || `memo-${Math.random()}`;
    const filename = memo.filename || memo.audioFilename || 'Unknown';
    const duration = memo.metadata?.duration || memo.duration || '0:00';

    let date = memo.parsedDate?.dateOnly;
    if (!date) {
      date = memo.date || memo.dateModified || memo.dateFormatted;
      if (!date && memo.stats?.mtime) {
        date = new Date(memo.stats.mtime).toISOString().split('T')[0];
      }
      if (!date) {
        date = new Date().toISOString().split('T')[0];
      }
    }

    const status = memo.status || 'unprocessed';

    let fileSize = memo.fileSize;
    if (!fileSize && memo.stats?.size) {
      const sizeMB = memo.stats.size / 1024 / 1024;
      fileSize = `${sizeMB.toFixed(2)}MB`;
    }
    if (!fileSize) {
      fileSize = 'Unknown';
    }

    let transcriptPath = memo.transcriptPath;
    if (!transcriptPath && (memo.hasTranscript || status === 'transcribed' || status === 'analyzed')) {
      const baseName = filename.replace(/\.(m4a|wav|m4b)$/, '');
      transcriptPath = `inbox/voice-memos/${baseName}.md`;
    }

    return {
      id,
      filename,
      title: memo.title,
      duration,
      date,
      status,
      fileSize,
      source: memo.source,
      hasTranscript: memo.hasTranscript || !!transcriptPath || status === 'transcribed' || status === 'analyzed',
      transcript: transcriptPath ? {
        filename: transcriptPath.split('/').pop() || '',
        path: transcriptPath,
        wordCount: memo.metadata?.wordCount || 0,
        duration: duration
      } : undefined,
      session: memo.session
    };
  });

  console.log('[getVoiceMemos] Transformed memos:', {
    count: transformedMemos.length,
    firstTransformed: transformedMemos[0]
  });

  return {
    success: data.success,
    total: data.counts?.total || transformedMemos.length,
    memos: transformedMemos,
    counts: data.counts
  };
}

/**
 * Upload voice memo files
 */
export async function uploadVoiceMemos(files: File[]): Promise<VoiceMemoUploadResponse> {
  const formData = new FormData();
  files.forEach(file => {
    const ext = file.name.toLowerCase().match(/\.([^.]+)$/)?.[1];
    const allowedTypes = ['m4a', 'm4b', 'wav', 'mp3'];
    if (!ext || !allowedTypes.includes(ext)) {
      throw new Error(`Unsupported file type: .${ext}. Supported types: ${allowedTypes.map(t => `.${t}`).join(', ')}`);
    }
    formData.append('audio', file);
  });

  const response = await fetchWithAuth(`${VOICE_MEMO_API_BASE}/api/voice-memos/upload`, {
    method: 'POST',
    body: formData,
    credentials: 'include'
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    const errorMessage = error.error || error.details || `Failed to upload files: ${response.statusText}`;
    console.error('Upload error details:', error);
    throw new Error(errorMessage);
  }

  return response.json();
}

/**
 * Process a single voice memo
 */
export async function processVoiceMemo(
  filename: string,
  source: 'uploaded' | 'system' = 'uploaded',
  options?: { folderPath?: string }
): Promise<VoiceMemoProcessResponse> {
  const response = await fetchWithAuth(`${VOICE_MEMO_API_BASE}/api/voice-memos/process`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      filename,
      source,
      folderPath: options?.folderPath,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || `Failed to process voice memo: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Batch process multiple voice memos
 */
export async function batchProcessVoiceMemos(
  filenames: string[],
  source: 'uploaded' | 'system' = 'uploaded'
): Promise<{
  success: boolean;
  totalRequested: number;
  processed: number;
  failed: number;
  results: Array<{
    filename: string;
    transcript: {
      filename: string;
      path: string;
      wordCount: number;
      duration: string;
    };
  }>;
  errors?: Array<{
    filename: string;
    error: string;
  }>;
  message: string;
}> {
  const response = await fetchWithAuth(`${VOICE_MEMO_API_BASE}/api/voice-memos/batch-process`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ filenames, source }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || `Failed to batch process voice memos: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Update voice memo title and optional summary
 */
export async function updateVoiceMemoTitle(
  id: string,
  title: string,
  summary?: string
): Promise<{ success: boolean; title: string; summary?: string }> {
  const response = await fetchWithAuth(`${VOICE_MEMO_API_BASE}/api/voice-memos/${id}/title`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ title, summary }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || `Failed to update voice memo title: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get audio URL for a voice memo
 */
export function getVoiceMemoAudioUrl(filename: string): string {
  return `${VOICE_MEMO_API_BASE}/api/voice-memos/audio/${encodeURIComponent(filename)}`;
}
